import { createRoute, Link } from "@tanstack/react-router";
import { LegalPlaceholderGuard } from "@/components/LegalPlaceholderGuard";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toSafeReactNode } from "@/lib/utils";
import { rootRoute } from "./__root";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-6 space-y-3">
      <h2 className="text-base font-semibold text-text">{toSafeReactNode(title)}</h2>
      <div className="text-sm text-text-muted leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function TermsPage() {
  const { t } = useTranslation("common");

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto max-w-2xl px-6 py-12 space-y-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-accent transition-colors"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Zurück
        </Link>

        <LegalPlaceholderGuard className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-text">Allgemeine Geschäftsbedingungen</h1>
          <p className="mt-2 text-sm text-text-muted">
            Zuletzt aktualisiert: {new Date().toLocaleDateString("de-DE")}
          </p>
        </div>

        <div className="space-y-4">

          <Section title="§ 1 Anbieter und Geltungsbereich">
            <p>
              Anbieter: <strong className="text-text">[DEIN VORNAME NACHNAME]</strong>,
              [STRASSE HAUSNUMMER], [PLZ ORT], Deutschland
              (E-Mail: support@takelead.app).
            </p>
            <p>
              Diese AGB gelten für alle Verträge zur Nutzung von TakeLead (Chrome-Extension
              und Web-Dashboard). Abweichende AGB des Nutzers werden nicht anerkannt.
            </p>
          </Section>

          <Section title="§ 2 Leistungsgegenstand">
            <p>
              TakeLead ist ein SaaS-Dienst zur Erfassung von Kontaktdaten von Webseiten
              und deren Übertragung in CRM-Systeme (HubSpot, Pipedrive, Salesforce, Weclapp).
              Der Dienst wird in verschiedenen Tarifplänen (Free, Starter, Pro, Team) angeboten.
            </p>
          </Section>

          <Section title="§ 3 Vertragsschluss und Registrierung">
            <p>
              Der Vertrag kommt durch Registrierung auf takelead.app und Bestätigung der
              E-Mail-Adresse zustande. Mit der Registrierung akzeptiert der Nutzer diese AGB
              und die Datenschutzerklärung.
            </p>
          </Section>

          <Section title="§ 4 Pflichten des Nutzers">
            <p>
              Der Nutzer ist verpflichtet, den Dienst ausschließlich rechtmäßig zu nutzen,
              insbesondere geltendes Datenschutzrecht (DSGVO, BDSG) bei der Erfassung
              personenbezogener Daten Dritter einzuhalten. Der Nutzer ist als verantwortliche
              Stelle (Art. 4 Nr. 7 DSGVO) für erfasste Kontaktdaten allein verantwortlich.
            </p>
            <p>
              Verboten sind: Massenhaftes automatisiertes Erfassen, Erfassung sensibler
              Datenkategorien (Art. 9 DSGVO), Spam, und die Weitergabe von Zugangsdaten.
            </p>
          </Section>

          <Section title="§ 5 Vergütung und Zahlung">
            <p>
              Der Free-Plan ist unentgeltlich. Kostenpflichtige Pläne werden monatlich oder
              jährlich im Voraus über Stripe abgerechnet. Alle Preise inkl. gesetzlicher MwSt.
              (soweit zutreffend). Bei Kleinunternehmerregelung (§ 19 UStG) wird keine MwSt.
              ausgewiesen.
            </p>
            <p>
              Preisänderungen werden 30 Tage im Voraus angekündigt.
            </p>
          </Section>

          <Section title="§ 6 Laufzeit und Kündigung">
            <p>
              Monatliche Abonnements verlängern sich automatisch monatlich, jährliche jährlich.
              Kündigung jederzeit über das Billing-Portal im Dashboard (takelead.app/billing)
              zum Ende der laufenden Periode.
            </p>
            <p>
              Nach Kündigung: Zugang bis Periodenende, danach Wechsel auf Free-Plan.
              Datenlöschung nach 30-tägiger Übergangsfrist.
            </p>
          </Section>

          <Section title="§ 7 Widerrufsrecht (Verbraucher)">
            <div className="rounded-lg border border-border bg-surface-2 p-4 space-y-2">
              <p className="font-medium text-text">Widerrufsbelehrung</p>
              <p>
                Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag
                zu widerrufen. Die Widerrufsfrist beträgt 14 Tage ab Vertragsschluss.
              </p>
              <p>
                Widerruf per E-Mail an:{" "}
                <a href="mailto:support@takelead.app" className="text-accent hover:underline">
                  support@takelead.app
                </a>
              </p>
              <p>
                Das Widerrufsrecht erlischt vorzeitig, wenn Sie ausdrücklich der sofortigen
                Vertragserfüllung zugestimmt haben und bestätigt haben, dass Sie Ihr
                Widerrufsrecht bei vollständiger Vertragserfüllung verlieren.
              </p>
            </div>
          </Section>

          <Section title="§ 8 Haftung">
            <p>
              Unbeschränkte Haftung für Vorsatz, grobe Fahrlässigkeit sowie Verletzung von
              Leben, Körper oder Gesundheit. Bei leicht fahrlässiger Verletzung wesentlicher
              Vertragspflichten: Haftung begrenzt auf vertragstypischen vorhersehbaren Schaden.
              Im Übrigen ausgeschlossen.
            </p>
            <p>
              Keine Haftung für Ausfälle von Drittdiensten (Supabase, Stripe, Vercel,
              CRM-Anbieter) die der Anbieter nicht zu vertreten hat.
            </p>
          </Section>

          <Section title="§ 9 Datenschutz und AVV">
            <p>
              Soweit der Nutzer personenbezogene Daten Dritter über TakeLead verarbeitet,
              gilt der Anbieter als Auftragsverarbeiter (Art. 28 DSGVO), der Nutzer als
              Verantwortlicher. Details in der{" "}
              <Link to="/privacy" className="text-accent hover:underline">Datenschutzerklärung</Link>.
            </p>
          </Section>

          <Section title="§ 10 Änderungen der AGB">
            <p>
              Änderungen werden mindestens 30 Tage vorab per E-Mail angekündigt.
              Kein Widerspruch innerhalb von 30 Tagen gilt als Zustimmung.
            </p>
          </Section>

          <Section title="§ 11 Schlussbestimmungen">
            <p>
              Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. Gerichtsstand
              für Unternehmer: Sitz des Anbieters. Für Verbraucher gilt der gesetzliche
              Gerichtsstand.
            </p>
          </Section>

        </div>
        </LegalPlaceholderGuard>

        <footer className="border-t border-border pt-6 text-center text-xs text-text-muted space-x-3">
          <Link to="/privacy" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.privacy"))}</Link>
          <span className="text-border">·</span>
          <Link to="/terms" className="hover:text-text transition-colors">{toSafeReactNode(t("footer.terms"))}</Link>
          <span className="text-border">·</span>
          <Link to="/lawful-use" className="hover:text-text transition-colors">Nutzungsrichtlinien</Link>
          <span className="text-border">·</span>
          <Link to="/impressum" className="hover:text-text transition-colors">Impressum</Link>
        </footer>
      </div>
    </div>
  );
}

export const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});
